import { Transition } from '@headlessui/react'
import { Fragment, useEffect } from 'react'

import { Pipeline } from '@ui/__generated__/graphql.ts'
import { PlusIcon } from '@ui/components/icons'
import { CodeTransformType } from '@ui/interfaces'
import { generateInitialCode } from '@ui/pages/pipelinesWorkflow/components/NodeTypes'
import clsx from 'clsx'

interface PipelineCardProps {
  pipeline: Pipeline
  navigate: (pipeline: Pipeline) => void
}

const PipelineCard = ({ pipeline, navigate }: PipelineCardProps) => {
  const totalDestinations = pipeline.nodes?.filter((node) => node?.type === 'EXPORT')
  return (
    <div
      className={clsx(
        'relative my-2.5 w-full cursor-pointer rounded-md py-6',
        'border border-gray-200 bg-white p-5 transition duration-300 ease-in-out',
        'hover:border-primary-600 hover:shadow-md'
      )}
      onClick={() => navigate(pipeline)}>
      <div className="absolute left-0 top-0 h-1 w-full rounded-t-md bg-primary-600" />
      <h1 className="text-lg font-medium">{pipeline.name}</h1>
      <p className="text-base text-gray-500">
        {totalDestinations?.length} Destination{totalDestinations?.length === 1 ? '' : 's'}
      </p>
    </div>
  )
}

interface PipelineListProps {
  pipelines: Pipeline[]
  event: string
  show: boolean
  setShowPipelineList: (show: boolean) => void
  transformType: CodeTransformType
  serviceName?: string
  name?: string
}

export const PipelineList = ({
  pipelines,
  event,
  show,
  setShowPipelineList,
  transformType,
  serviceName,
  name,
}: PipelineListProps) => {
  // Handles the pipeline list dropdown click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest(`#pipeline-list-${transformType}`) && show) {
        setShowPipelineList(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [show, setShowPipelineList, transformType])

  const handlePipelineNavigate = (pipeline: Pipeline | undefined) => {
    // clean json event so it pareses correctly, replace single quotes with double quotes, removing dangling commas
    const cleanEvent = event.replace(/'/g, '"').replace(/,(\s+)?}/g, '}')

    const code = generateInitialCode({ type: transformType, serviceName, name })

    // open in a new tab with the pipeline id
    if (!pipeline) {
      return window.open(
        `/pipelines/new?transform=true&event=${encodeURIComponent(cleanEvent)}&code=${encodeURIComponent(code)}`,
        '_blank',
        'noopener noreferrer'
      )
    }

    window.open(
      `/pipelines/${pipeline.id}?transform=true&event=${encodeURIComponent(cleanEvent)}&code=${encodeURIComponent(
        code
      )}`,
      '_blank',
      'noopener noreferrer'
    )
  }

  const handleMessage = () => {
    switch (transformType) {
      case CodeTransformType.CODE:
        return 'Add code to an existing pipeline or create a new one.'
      case CodeTransformType.DROP:
        return 'Drop trace to an existing pipeline or create a new one.'
      case CodeTransformType.SAMPLE:
        return 'Sample trace to an existing pipeline or create a new one.'
    }
  }

  return (
    <Transition
      as={Fragment}
      show={show}
      enter="opacity-0 transform transition ease-in-out duration-100"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="opacity-0 transform transition ease-in-out duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-70">
      <div
        className="absolute right-0 z-30 mr-2 w-[315px] rounded-md border bg-white p-5 shadow-md"
        id={`pipeline-list-${transformType}`}>
        <h1 className="mb-1 text-xl font-medium	">Select a pipeline</h1>
        <p className="text-base text-gray-500">{handleMessage()}</p>
        <div className="mt-4 flex max-h-[60vh] flex-wrap overflow-y-auto">
          <div
            onClick={() => handlePipelineNavigate(undefined)}
            className={clsx(
              'flex cursor-pointer flex-row items-center justify-center rounded-md p-10',
              'border-1 my-2.5 w-full border border-primary-600 bg-white transition duration-300 ease-in-out',
              'hover:shadow-md'
            )}>
            <PlusIcon className="mr-1.5 h-5 w-5 text-primary-600" />
            <h2 className="text-base font-medium text-primary-600">New pipeline</h2>
          </div>
          {pipelines.map((pipeline) => (
            <PipelineCard key={pipeline.id} pipeline={pipeline} navigate={handlePipelineNavigate} />
          ))}
        </div>
      </div>
    </Transition>
  )
}
